import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inquiries() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Inquiry sent — we'll be in touch.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquiries"
      data-testid="inquiries-section"
      className="bg-black text-white border-t border-black/10 py-24 md:py-40 lg:py-48"
    >
      <Toaster position="bottom-center" richColors />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="col-span-12 md:col-span-5"
          >
            <span className="label !text-white">Inquiries</span>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white mt-8">
              Get
              <br />
              in <span className="italic font-light">Touch.</span>
            </h2>
            <p className="font-body font-light text-base md:text-lg text-white/70 mt-10 max-w-md leading-relaxed">
              For collaborations, press, artist submissions or to be added to our
              invitation list — write to us. We read everything.
            </p>
            <div className="mt-12 space-y-2">
              <div className="label !text-white/60">Email</div>
              <a
                href="mailto:info@nygprojects.com"
                className="font-body text-lg text-white hover:text-white/70 transition-colors"
                data-testid="contact-email-link"
              >
                info@nygprojects.com
              </a>
            </div>
            <div className="mt-8 space-y-2">
              <div className="label !text-white/60">Instagram</div>
              <a
                href="https://instagram.com/nyg_projects"
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-lg text-white hover:text-white/70 transition-colors"
                data-testid="contact-instagram-link"
              >
                @nyg_projects
              </a>
            </div>
          </motion.div>

          {/* Right - form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, delay: 0.15 }}
            onSubmit={handleSubmit}
            data-testid="inquiry-form"
            className="col-span-12 md:col-span-6 md:col-start-7 mt-4 md:mt-12"
          >
            <div className="space-y-10">
              <div>
                <label className="label !text-white/60 block mb-3" htmlFor="name">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  data-testid="inquiry-name-input"
                  className="w-full bg-transparent border-0 border-b border-white/30 focus:border-white outline-none px-0 py-3 text-white text-lg font-light placeholder-white/30 transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="label !text-white/60 block mb-3" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="inquiry-email-input"
                  className="w-full bg-transparent border-0 border-b border-white/30 focus:border-white outline-none px-0 py-3 text-white text-lg font-light placeholder-white/30 transition-colors"
                  placeholder="you@email.com"
                />
              </div>

              <div>
                <label className="label !text-white/60 block mb-3" htmlFor="message">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  data-testid="inquiry-message-input"
                  className="w-full bg-transparent border-0 border-b border-white/30 focus:border-white outline-none px-0 py-3 text-white text-lg font-light placeholder-white/30 resize-none transition-colors"
                  placeholder="Tell us a little..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit-btn"
                className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-white/85 transition-all disabled:opacity-50"
              >
                {submitting ? "Sending..." : submitted ? "Send Another" : "Send Inquiry"}
                <ArrowUpRight
                  size={16}
                  strokeWidth={1}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>

              {submitted && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="font-body text-sm text-white/70 italic"
                  data-testid="inquiry-success-message"
                >
                  Thank you — your message has reached us.
                </motion.p>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
